import classNames from 'classnames';
import { type CSSProperties, memo, type MouseEvent as ReactMouseEvent, useState } from 'react';

import { type Clip } from '@air/api/types';
import { type FileExtensionPreviewSize } from '@air/component-file-extension-preview';
import { ImgixImage } from '@air/component-imgix-image';
import { ClipUtils, getAssetPreviewType, type WebClipType } from '@air/utils-clips';
import { cn } from '@air/utils-tailwind';

import { NO_PREVIEW_AVAILABLE, NoPreviewImage } from '~/components/UI/NoPreviewImage';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { getPreferenceImgixFit, type ThumbnailPreference } from '~/hooks/useThumbnailPreference';
import { ClipIconSize } from '~/types/clip-icon-size';

type GetNoPreviewTextOptions = {
  ext: Clip['ext'] | null;
  status: Clip['status'] | null;
  webClipType?: WebClipType;
  shouldTruncate?: boolean;
};

/**
 * Get the appropriate preview text for a clip based on its fields
 */
const getNoPreviewText = ({ ext, status, webClipType, shouldTruncate = false }: GetNoPreviewTextOptions) => {
  const getNonMediaPreviewText = (ext: Clip['ext'] | null) => ext || '';

  const truncatePreviewText = (text: string) => {
    return text.length < 8 ? text : '…';
  };

  if (webClipType === 'file' || ClipUtils.isNonTranscodable(status)) {
    const previewText = getNonMediaPreviewText(ext);
    return shouldTruncate ? truncatePreviewText(previewText) : previewText;
  }

  if (ClipUtils.isFailed(status)) {
    return NO_PREVIEW_AVAILABLE;
  }

  return '';
};

type ClipThumbnailProps = {
  /** URI for the image to be displayed in the clip thumbnail */
  src: string;
  /** URI for the animated image to be displayed on hover for a clip e.g an animated preview on video hover */
  animatedSrc?: string;

  /** Size of the icon to be used in the NoPreviewImage fallback component */
  iconSize?: ClipIconSize;
  /** Border radius to be used in the NoPreviewImage fallback component */
  borderRadius?: number;
  /** Denotes whether or not the NoPreviewImage fallback component should include a text display */
  hideText?: boolean;
  style?: CSSProperties;
  size?: FileExtensionPreviewSize;
  index?: number;
  onClick?: (e: ReactMouseEvent) => void;
  height?: number;
  width?: number;
  videoUrl?: Clip['assets']['video'];
  className?: string;
  thumbnailPreference?: ThumbnailPreference;
} & Pick<Clip, 'status' | 'type' | 'ext' | 'description' | 'importedName'>;

/** Displays preview image for a clip.
 * If no preview is available a NoPreviewImage component is rendered with styles and metadata appropriate for the clip's WebClipType
 * */
export const ClipThumbnail = memo(
  ({
    status,
    src,
    animatedSrc,
    type,
    ext,
    description,
    borderRadius = 6,
    iconSize = 48,
    hideText = false,
    size,
    height = 400,
    width = 400,
    className,
    thumbnailPreference = 'default',
    ...rest
  }: ClipThumbnailProps) => {
    const [isLoaded, setLoaded] = useState(false);

    const processing = ClipUtils.isProcessing(status);
    const isTranscodeFinished = ClipUtils.isTranscodeFinished(status);
    const renderNoPreviewImage = (!src && (isTranscodeFinished || processing)) || !ClipUtils.canHavePreviewImage(type);

    if (renderNoPreviewImage) {
      const webClipType = getAssetPreviewType({ type, ext });

      return (
        <NoPreviewImage
          processing={processing}
          iconSize={iconSize}
          borderRadius={borderRadius}
          hideText={hideText || (processing && iconSize <= ClipIconSize.small)}
          type={webClipType}
          customText={getNoPreviewText({ ext, status, webClipType, shouldTruncate: true })}
          ext={ext}
          size={size}
          color={processing ? 'gray' : 'color'}
          {...rest}
        />
      );
    }

    const alt = description || '';
    const imgClassName = classNames(
      'block max-w-full',
      isLoaded ? 'opacity-100' : 'opacity-0',
      thumbnailPreference === 'contain' ? 'h-full' : 'h-auto',
      thumbnailPreference === 'default' && 'object-cover',
      thumbnailPreference === 'contain' && 'object-contain',
    );

    return (
      <div
        className={cn(
          'pointer-events-none absolute top-0 left-0 flex h-full w-full items-center justify-center overflow-hidden',
          isLoaded ? 'bg-grey-3' : 'bg-grey-7',
          className,
        )}
        role="button"
        style={{ borderRadius }}
        {...rest}
      >
        {animatedSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img className={imgClassName} alt={alt} src={animatedSrc} style={{ borderRadius }} />
        ) : (
          <ImgixImage
            className={imgClassName}
            alt={alt}
            fallbackUrl={getCloudFrontUrlFromImgixUrl(src)}
            src={src}
            imgixParams={{
              fit: getPreferenceImgixFit(thumbnailPreference),
              cs: 'origin',
            }}
            width={width}
            height={height}
            onLoad={() => setLoaded(true)}
            style={{ borderRadius }}
          />
        )}
      </div>
    );
  },
);

ClipThumbnail.displayName = 'ClipThumbnail';
